import React, { useState, useEffect, Fragment, useCallback } from "react";
import { Modal, Col, Row, Form, Table, Button } from "react-bootstrap";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Select from "react-select";
import toast from "react-hot-toast";
import ReactLoading from "react-loading";

// api calls
import {
  getPettyCashOutDetailTransaction,
  getPettyCashInDetailTransaction,
  updatePettyCashOutDetailTransaction,
  updatePettyCashInDetailTransaction,
  deletePettyCashTransactionDetails,
} from "../../Helpers/apiCalls/PettyCash/PettyCashRegisterApi";
import { getAllUsers } from "../../Helpers/apiCalls/usersApi";
import { getAllExpenseType } from "../../Helpers/apiCalls/expensetypesApi";

// validation
import {
  validatePettyCashOutUpdate,
  validatePettyCashInCreate,
} from "../../Helpers/Validation/PettyCash/PettyCashValidation";

// utils
import {
  getTodayDateISO,
  toastStyle,
  selectDropdownStyle,
} from "../../Helpers/Utils/Common";

import trash from "./../../Assets/Images/trash.png";

// components
import DeleteModal from "../../Components/Modals/DeleteModal";

// css
import "./PettyCash.css";

export default function PettyCashEditModal({ show, onHide, transaction, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [isError, setIsError] = useState({});

  const [pettyCashDetails, setPettyCashDetails] = useState({
    petty_cash_id: "1",
    petty_cash_detail_id: "",
    type: "",
    particulars: "",
    remarks: "",
    invoice_no: "",
    out_type: "",
    from: "",
    date: getTodayDateISO(),
    item_names: [],
    item_quantities: [],
    item_prices: [],
    item_units: [],
    amount: "0.00",
    requested_by_name: "",
    requested_by_id: "",
    expense_type_id: "",
    expense_type_name: "",
  });

  const [orderItems, setOrderItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedExpenseType, setSelectedExpenseType] = useState(null);

  // Helper function to format numbers with commas
const formatNumberWithCommas = (number) => {
  if (!number && number !== 0) return "0.00";
  const num = parseFloat(number);
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

  // Reset editing state when modal opens/closes
  useEffect(() => {
    if (show) {
      setIsEditing(false);
      setIsError({});
      // Don't reset orderItems here - let fetchTransactionDetails handle it
      setSelectedUser(null); // Reset selected user
      setSelectedExpenseType(null); // Reset selected expense type
    }
  }, [show]);



  const fetchTransactionDetails = useCallback(async (transactionType) => {
    if (!transaction) return;

    setIsLoading(true);
    try {
      let response;
      if (transactionType === "out") {
        response = await getPettyCashOutDetailTransaction(transaction.id);
      } else {
        response = await getPettyCashInDetailTransaction(transaction.id);
      }

      if (response && response.status === "success") {
        // API returns data in array format with index "0"
        const data = response.data["0"] || response.data;

        // Update the details while preserving the transaction type
        setPettyCashDetails(prevDetails => ({
          ...prevDetails,
          petty_cash_detail_id: data.id || transaction.id,
          type: transactionType,
          particulars: data.particulars || "",
          remarks: data.remarks || "",
          invoice_no: data.invoice_no || "",
          date: data.date || prevDetails.date,
          amount: data.amount || "0.00",
          requested_by_name: data.requested_by_name || "",
          requested_by_id: data.requested_by || data.requested_by_id || "",
          expense_type_id: data.out_type || data.expense_type_id || "",
          expense_type_name: data.expense_type_name || "",
          from: data.from || "",
        }));

        // Set order items for cash out transactions
        if (transactionType === "out") {
          if (data.petty_cash_items && data.petty_cash_items.length > 0) {
            const formattedItems = data.petty_cash_items.map(item => ({
              name: item.name,
              qty: parseFloat(item.qty),
              unit: item.unit,
              price: parseFloat(item.price),
            }));
            setOrderItems(formattedItems);
          } else {
            // Initialize empty array if no items
            setOrderItems([]);
          }
        }

        // Set selected values for dropdowns
        if (data.requested_by_name && (data.requested_by || data.requested_by_id)) {
          setSelectedUser({
            value: data.requested_by || data.requested_by_id,
            label: data.requested_by_name,
          });
        }

        if (data.expense_type_name && (data.out_type || data.expense_type_id)) {
          setSelectedExpenseType({
            value: data.out_type || data.expense_type_id,
            label: data.expense_type_name,
          });
        }
      }
    } catch (error) {
      toast.error("Error loading transaction details", { style: toastStyle() });
    }
    setIsLoading(false);
  }, [transaction]);

  // Load transaction data when modal opens
  useEffect(() => {
    if (show && transaction) {
      // Use the transaction type directly from the data
      const transactionType = transaction.type || "in";

      // Get the amount based on transaction type
      let amount = "0.00";
      if (transactionType === "out") {
        // For cash out, get amount from cashout_amount or amount field
        amount = transaction.cashout_amount || transaction.amount || "0.00";
        // Remove formatting (commas) if present
        if (typeof amount === 'string') {
          amount = amount.replace(/,/g, '');
        }
      } else {
        // For cash in, get amount from cashin_amount or amount field
        amount = transaction.cashin_amount || transaction.amount || "0.00";
        // Remove formatting (commas) if present
        if (typeof amount === 'string') {
          amount = amount.replace(/,/g, '');
        }
      }

      const newPettyCashDetails = {
        petty_cash_id: transaction.petty_cash_id || "1",
        petty_cash_detail_id: transaction.id || transaction.petty_cash_detail_id || "",
        type: transactionType,
        particulars: transaction.particulars || "",
        remarks: transaction.remarks || "",
        invoice_no: transaction.invoice_no || "",
        out_type: transaction.expense_type_id || transaction.out_type || "",
        from: transaction.from || "",
        date: transaction.date || getTodayDateISO(),
        amount: amount,
        requested_by_name: transaction.requested_by_name || "",
        requested_by_id: transaction.requested_by_id || transaction.requested_by || "",
        expense_type_id: transaction.expense_type_id || "",
        expense_type_name: transaction.expense_type_name || "",
        added_by: transaction.added_by || "",
        added_by_name: transaction.added_by_name || "",
        added_on: transaction.added_on || "",
        approved_by: transaction.approved_by || "",
        approved_on: transaction.approved_on || "",
        status: transaction.status || "",
        current: transaction.current || "",
        item_names: [],
        item_quantities: [],
        item_prices: [],
        item_units: [],
      };




      setPettyCashDetails(newPettyCashDetails);

      // Set selected values for dropdowns
      if (transaction.requested_by_name && transaction.requested_by_id) {
        setSelectedUser({
          value: transaction.requested_by_id,
          label: transaction.requested_by_name,
        });
      }

      if (transaction.expense_type_name && transaction.expense_type_id) {
        setSelectedExpenseType({
          value: transaction.expense_type_id,
          label: transaction.expense_type_name,
        });
      }

      // Items will be loaded by fetchTransactionDetails

      // Fetch additional details if needed
      fetchTransactionDetails(transactionType);
    }
  }, [show, transaction, fetchTransactionDetails]);

  // Fetch users and expense types
  useEffect(() => {
    if (show) {
      fetchUsers();
      fetchExpenseTypes();
    }
  }, [show]);

  async function fetchUsers() {
    try {
      const response = await getAllUsers();
      if (response && response.data) {
        const userOptions = response.data.map((user) => ({
          value: user.id,
          label: `${user.first_name} ${user.middle_name} ${user.last_name}`,
        }));
        setUsers(userOptions);
      }
    } catch (error) {
      // Error fetching users
    }
  }

  async function fetchExpenseTypes() {
    try {
      const response = await getAllExpenseType();
      if (response && response.data) {
        const typeOptions = response.data.map((type) => ({
          value: type.id,
          label: type.name,
        }));
        setExpenseTypes(typeOptions);
      }
    } catch (error) {
      // Error fetching expense types
    }
  }

  // Handle form changes
  function handleInputChange(e) {
    const { name, value } = e.target;
    setPettyCashDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleDateChange(dateString) {
    setPettyCashDetails((prev) => ({
      ...prev,
      date: dateString,
    }));
  }

  function handleUserChange(selectedOption) {
    setSelectedUser(selectedOption);
    setPettyCashDetails((prev) => ({
      ...prev,
      requested_by_id: selectedOption?.value || "",
      requested_by_name: selectedOption?.label || "",
    }));
  }

  function handleExpenseTypeChange(selectedOption) {
    setSelectedExpenseType(selectedOption);
    setPettyCashDetails((prev) => ({
      ...prev,
      expense_type_id: selectedOption?.value || "",
      out_type: selectedOption?.value || "",
    }));
  }

  // Item management functions - matching form approach
  function addItem() {
    const newItem = {
      name: "",
      qty: 1,
      unit: "",
      price: 0,
    };
    setOrderItems([...orderItems, newItem]);
  }

  function removeItem(index) {
    const newItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(newItems);

    // Update total amount
    const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    setPettyCashDetails(prev => ({ ...prev, amount: newTotal.toFixed(2) }));
  }

  // Handle edit/save
  async function handleEdit() {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    // Save changes
    setIsLoading(true);
    try {
      let response;
      if (pettyCashDetails.type === "out") {
        // Prepare data for Cash Out transaction
        const cashOutData = {
          ...pettyCashDetails,
          item_names: orderItems.map(item => item.name),
          item_quantities: orderItems.map(item => item.qty.toString()),
          item_prices: orderItems.map(item => item.price.toString()),
          item_units: orderItems.map(item => item.unit),
          requested_by: selectedUser?.value || pettyCashDetails.requested_by_id,
        };

        if (validatePettyCashOutUpdate(cashOutData, setIsError)) {
          response = await updatePettyCashOutDetailTransaction(cashOutData);
        } else {
          setIsLoading(false);
          toast.error("Please fill in all required fields", { style: toastStyle() });
          return;
        }
      } else {
        if (validatePettyCashInCreate(pettyCashDetails, setIsError)) {
          response = await updatePettyCashInDetailTransaction(pettyCashDetails);
        } else {
          setIsLoading(false);
          toast.error("Please fill in all required fields", { style: toastStyle() });
          return;
        }
      }

      if (response && response.status === "success") {
        toast.success("Transaction updated successfully", { style: toastStyle() });
        setIsEditing(false);
        onUpdate(); // Refresh the parent component
        onHide(); // Close modal
      } else {
        toast.error(response?.data?.response || "Error updating transaction", {
          style: toastStyle(),
        });
      }
    } catch (error) {
      toast.error("Error updating transaction", { style: toastStyle() });
    }
    setIsLoading(false);
  }

  // Handle delete
  async function handleDelete() {
    if (isDeleteClicked) return;

    setIsDeleteClicked(true);
    try {
      const response = await deletePettyCashTransactionDetails(transaction.id);
      if (response && response.status === "success") {
        toast.success("Transaction deleted successfully", { style: toastStyle() });
        setShowDeleteModal(false);
        onUpdate(); // Refresh the parent component
        onHide(); // Close modal
      } else {
        toast.error("Error deleting transaction", { style: toastStyle() });
      }
    } catch (error) {
      toast.error("Error deleting transaction", { style: toastStyle() });
    }
    setIsDeleteClicked(false);
  }

  function handleClose() {
    setIsEditing(false);
    setIsError({});
    onHide();
  }

  if (!transaction) return null;

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className="page-title">
            {isEditing
              ? (pettyCashDetails.type === "out" ? "EDIT PETTY CASH OUT TRANSACTION" : "EDIT PETTY CASH IN TRANSACTION")
              : (pettyCashDetails.type === "out" ? "PETTY CASH OUT TRANSACTION" : "PETTY CASH IN TRANSACTION")
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <div className="d-flex justify-content-center my-5">
              <ReactLoading type="bubbles" color="#5ac8e1" height={50} width={50} />
            </div>
          ) : (
            <div className="edit-form">
              <Fragment>
                {pettyCashDetails.type === "out" ? (
                  // Cash Out Layout - exactly matching PettyCashCashOutForm.js
                  <>
                    <Row className="mt-4">
                      <Col xs={4}>
                        <span className="edit-label">
                          Cash Out Date
                          <span className="color-red"> *</span>
                        </span>
                        <DatePicker
                          className="nc-modal-custom-text-new-datepicker"
                          name="date"
                          value={pettyCashDetails.date && pettyCashDetails.date !== "0000-00-00" ? dayjs(pettyCashDetails.date) : null}
                          format="YYYY-MM-DD"
                          onChange={(date, dateString) => handleDateChange(dateString)}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col xs={4}>
                        <span className="edit-label">Invoice No.</span>
                        <Form.Control
                          type="text"
                          name="invoice_no"
                          className="nc-modal-custom-text"
                          value={pettyCashDetails.invoice_no || ""}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col xs={4}>
                        <span className="edit-label">
                          Account Type
                          <span className="color-red"> *</span>
                        </span>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select Expense Type..."
                          value={selectedExpenseType}
                          options={expenseTypes}
                          onChange={handleExpenseTypeChange}
                          styles={selectDropdownStyle}
                          isDisabled={!isEditing}
                        />
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col>
                        <span className="edit-label">PARTICULARS</span>
                        <span className="color-red"> *</span>
                        <Form.Control
                          type="text"
                          name="particulars"
                          className="nc-modal-custom-text"
                          value={pettyCashDetails.particulars || ""}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col>
                        <span className="edit-label">REQUESTED BY</span>
                        <span className="color-red"> *</span>
                        <Select
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="Select..."
                          value={selectedUser}
                          options={users}
                          onChange={handleUserChange}
                          styles={selectDropdownStyle}
                          isDisabled={!isEditing}
                        />
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col>
                        <span className="edit-label">REMARKS</span>
                        <Form.Control
                          type="text"
                          name="remarks"
                          className="nc-modal-custom-text"
                          value={pettyCashDetails.remarks || ""}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Col>
                    </Row>
                  </>
                ) : (
                  // Cash In Layout - exactly matching PettyCashCashInForm.js
                  <>
                    <Row className="mt-4">
                      <Col xs={4}>
                        <span className="edit-label">
                          Cash In Date
                          <span className="color-red"> *</span>
                        </span>
                        <DatePicker
                          className="nc-modal-custom-text-new-datepicker"
                          name="date"
                          value={pettyCashDetails.date && pettyCashDetails.date !== "0000-00-00" ? dayjs(pettyCashDetails.date) : null}
                          format="YYYY-MM-DD"
                          onChange={(date, dateString) => handleDateChange(dateString)}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col xs={4}>
                        <span className="edit-label">
                          FROM<span className="color-red"> *</span>
                        </span>
                        <Form.Control
                          type="text"
                          name="from"
                          className="nc-modal-custom-text"
                          placeholder="Select a bank"
                          value={pettyCashDetails.from || ""}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Col>
                      <Col xs={4}>
                        <span className="edit-label">
                          Amount<span className="color-red"> *</span>
                        </span>
                        <Form.Control
                          type="number"
                          name="amount"
                          className="nc-modal-custom-text"
                          value={pettyCashDetails.amount || ""}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col>
                        <span className="edit-label">REMARKS</span>
                        <Form.Control
                          as="textarea"
                          name="remarks"
                          className="nc-modal-custom-text"
                          value={pettyCashDetails.remarks || ""}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                        />
                      </Col>
                    </Row>

                    {/* GRAND TOTAL SECTION FOR CASH IN */}
                    <div className="grand-total-section mt-4">
                      <span className="grand-total-label">GRAND TOTAL</span>
                      <span className="grand-total-currency">PHP</span>
                      <span className="grand-total-amount">
                        {formatNumberWithCommas(pettyCashDetails.amount || 0)}
                      </span>
                    </div>
                  </>
                )}

                {pettyCashDetails.type === "out" && (
                  <>
                    {/* List Particulars Section - exactly matching PettyCashCashOutForm.js */}
                    <Row className="mt-4 pt-3">
                      <Col>
                        <span className="edit-label mb-2">
                          List Particulars
                          <span className="color-red"> *</span>
                        </span>
                        <div className="edit-purchased-items">
                          {orderItems.length !== 0 ? (
                            <Table>
                              <thead>
                                <tr>
                                  <th className="color-gray">item</th>
                                  <th className="color-gray">quantity</th>
                                  <th className="color-gray">unit</th>
                                  <th className="color-gray">price</th>
                                  <th className="color-gray">total</th>
                                  {isEditing && <th className="color-gray">actions</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {orderItems.map((item, index) => (
                                  <tr key={index}>
                                    <td>
                                      {isEditing ? (
                                        <Form.Control
                                          type="text"
                                          name="item_name"
                                          value={item.name}
                                          onChange={(e) => {
                                            const newItems = [...orderItems];
                                            newItems[index].name = e.target.value;
                                            setOrderItems(newItems);
                                          }}
                                        />
                                      ) : (
                                        item.name
                                      )}
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <Form.Control
                                          type="number"
                                          name="qty"
                                          value={item.qty}
                                          onChange={(e) => {
                                            const newItems = [...orderItems];
                                            newItems[index].qty = parseFloat(e.target.value) || 0;
                                            setOrderItems(newItems);
                                            // Update total amount
                                            const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
                                            setPettyCashDetails(prev => ({ ...prev, amount: newTotal.toFixed(2) }));
                                          }}
                                        />
                                      ) : (
                                        item.qty
                                      )}
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <Form.Control
                                          type="text"
                                          name="unit"
                                          value={item.unit}
                                          onChange={(e) => {
                                            const newItems = [...orderItems];
                                            newItems[index].unit = e.target.value;
                                            setOrderItems(newItems);
                                          }}
                                        />
                                      ) : (
                                        item.unit
                                      )}
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          name="price"
                                          value={item.price}
                                          onChange={(e) => {
                                            const newItems = [...orderItems];
                                            newItems[index].price = parseFloat(e.target.value) || 0;
                                            setOrderItems(newItems);
                                            // Update total amount
                                            const newTotal = newItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
                                            setPettyCashDetails(prev => ({ ...prev, amount: newTotal.toFixed(2) }));
                                          }}
                                        />
                                      ) : (
                                        formatNumberWithCommas(item.price)
                                      )}
                                    </td>
                                    <td className="color-green">
                                      {formatNumberWithCommas(parseFloat(item.price) * parseFloat(item.qty))}
                                    </td>
                                    {isEditing && (
                                      <td className="text-center">
                                        <img
                                          src={trash}
                                          onClick={() => removeItem(index)}
                                          className="cursor-pointer"
                                          alt="Delete"
                                          style={{ width: 20, height: 20 }}
                                        />
                                      </td>
                                    )}
                                    </tr>
                                  ))}

                              </tbody>
                            </Table>
                          ) : (
                            <div className="entries-not-found">
                              There is no ordered items yet.
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* ADD ITEM BUTTON - exactly matching form */}
                    {isEditing && (
                      <Row className="pt-3 PO-add-item">
                        <Button type="button" disabled={false} onClick={() => addItem()}>
                          Add Item
                        </Button>
                      </Row>
                    )}

                    {/* GRAND TOTAL SECTION */}
                    <div className="grand-total-section mt-3">
                      <span className="grand-total-label">GRAND TOTAL</span>
                      <span className="grand-total-currency">PHP</span>
                      <span className="grand-total-amount">
                        {orderItems.length > 0
                          ? formatNumberWithCommas(orderItems.reduce((sum, item) => sum + (parseFloat(item.price) * parseFloat(item.qty)), 0))
                          : formatNumberWithCommas(pettyCashDetails.amount || 0)
                        }
                      </span>
                    </div>
                  </>
                )}




              </Fragment>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
          <button
            type="button"
            className="button-secondary me-3"
            onClick={handleClose}
          >
            Close
          </button>
          <button
            type="button"
            className="button-warning-fill me-3"
            onClick={() => setShowDeleteModal(true)}
            disabled={isLoading}
          >
            Delete
          </button>
          <button
            type="button"
            className="button-primary"
            onClick={handleEdit}
            disabled={isLoading}
          >
            {isLoading ? (
              <ReactLoading type="bubbles" color="#FFFFFF" height={20} width={20} />
            ) : isEditing ? (
              "Save"
            ) : (
              "Edit"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        text="petty cash transaction"
        onDelete={handleDelete}
      />
    </>
  );
}